import { CircleHelp, Info } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGasPriceQuery, useGetTokenPriceQuery } from "@/data/queries";
import {
  GAS_CALCULATOR_NETWORKS,
  GasRangeByNetwork,
  Network,
  NetworkNameByChainId,
} from "@/lib/chains";

const MINT_100_GAS = 2620000;
const MINT_MIN = 120000;
const DEPLOY_GAS = 3110000;
const TRANSFER_GAS = 100000;
const METADATA_UPDATE_GAS = 600000;

const TOKEN_LOW_PRICE_MULTIPLIER = 0.5;

const GWEI_TO_ETH = 1e9;

export const GasCalculator = () => {
  const [selectedChain, setSelectedChain] = useState(Network.Avalanche);
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [estimatedGasPrice, setEstimatedGasPrice] = useState(0);
  const [nftCount, setNftCount] = useState(1000);
  const { data: tokenPrice } = useGetTokenPriceQuery(
    selectedChain,
    setEstimatedPrice,
  );
  useGasPriceQuery(selectedChain, setEstimatedGasPrice);

  return (
    <div className="space-y-6">
      <h2 className="scroll-m-20 text-3xl font-medium tracking-tight first:mt-0">
        Gas Cost Calculator
      </h2>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle className="text-base">What is gas?</AlertTitle>
        <AlertDescription className="text-sm">
          Gas is the cost required to process and validate data on the
          blockchain. It’s essentially the "fuel" needed to run the operation,
          and is paid to operators of servers, or validators, as compensation
          for their work.
        </AlertDescription>
      </Alert>

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight">
        Token and Gas Cost
      </h3>

      <Card className="p-6 space-y-6">
        <div className="space-y-2">
          <Label className="inline-block">Blockchain</Label>
          <Select
            value={selectedChain.toString()}
            onValueChange={(value) => setSelectedChain(Number(value))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select the chain" />
            </SelectTrigger>
            <SelectContent>
              {GAS_CALCULATOR_NETWORKS.map((blockchain) => (
                <SelectItem key={blockchain} value={blockchain.toString()}>
                  {NetworkNameByChainId[blockchain]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            Select the blockchain to estimate gas for.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="inline-block">Token Price in USD</Label>
          <Input
            value={estimatedPrice}
            onChange={(event) => {
              setEstimatedPrice(Number(event.target.value));
            }}
          />
          <p className="text-sm text-muted-foreground">
            Current market price of the token in USD. Historically $
            {tokenPrice?.symbol.toUpperCase()} is between $
            {(
              (tokenPrice?.market_data.current_price.usd || 0) *
              TOKEN_LOW_PRICE_MULTIPLIER
            ).toFixed(2)}{" "}
            and ${tokenPrice?.market_data.ath.usd.toFixed(2)}
          </p>
        </div>

        <div className="space-y-2">
          <Label className="inline-block">Gas Price in Tokens</Label>
          <Input
            value={estimatedGasPrice}
            onChange={(event) => {
              setEstimatedGasPrice(Number(event.target.value));
            }}
          />
          <p className="text-sm text-muted-foreground">
            Current gas price for guaranteed on-chain execution in the next
            block. Historically, {NetworkNameByChainId[selectedChain]} is
            between {GasRangeByNetwork[selectedChain][0]} and{" "}
            {GasRangeByNetwork[selectedChain][1]} gwei / gas unit.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="inline-block">Number of Items</Label>
          <Input
            value={nftCount}
            onChange={(event) => {
              setNftCount(Number(event.target.value));
            }}
          />
          <p className="text-sm text-muted-foreground">
            Number of items to mint NFTs for.
          </p>
        </div>

        <div className="flex gap-2 items-center text-muted-foreground">
          <CircleHelp className="w-5 h-5" />
          <p className="text-sm font-medium">
            Please refer to{" "}
            <a
              href="https://www.coingecko.com"
              target="_blank"
              className="text-black underline"
            >
              CoinGecko
            </a>{" "}
            for current and historic token prices. Gas prices can be found on{" "}
            <a
              href="https://owlracle.info"
              target="_blank"
              className="text-black underline"
            >
              Owlracle
            </a>
            .
          </p>
        </div>
      </Card>

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight">
        Estimated Cost
      </h3>

      <Card className="p-6 space-y-6">
        <div className="space-y-2">
          <Label className="inline-block">
            Total Cost to Mint and Transfer NFTs in USD
          </Label>
          <Input
            value={(
              (estimatedPrice *
                (estimatedGasPrice *
                  (nftCount * TRANSFER_GAS +
                    Math.max((nftCount * MINT_100_GAS) / 100, MINT_MIN) +
                    METADATA_UPDATE_GAS +
                    DEPLOY_GAS))) /
              GWEI_TO_ETH
            ).toPrecision(4)}
          />
          <p className="text-sm text-muted-foreground">
            Estimate includes NFT contract deployment, metadata updates,
            minting, and transfers.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="inline-block">
            Total Cost to Mint and Transfer NFTs in Tokens
          </Label>
          <Input
            value={(
              (estimatedGasPrice *
                (nftCount * TRANSFER_GAS +
                  Math.max((nftCount * MINT_100_GAS) / 100, MINT_MIN) +
                  METADATA_UPDATE_GAS +
                  DEPLOY_GAS)) /
              GWEI_TO_ETH
            ).toPrecision(4)}
          />
          <p className="text-sm text-muted-foreground">
            Estimate includes NFT contract deployment, metadata updates,
            minting, and transfers.
          </p>
        </div>

        <div className="flex gap-2 items-center text-muted-foreground">
          <CircleHelp className="w-5 h-5" />
          <p className="text-sm font-medium">
            Actual cost can range from{" "}
            <span className="text-black">
              {(
                (GasRangeByNetwork[selectedChain][0] *
                  (nftCount * TRANSFER_GAS +
                    Math.max((nftCount * MINT_100_GAS) / 100, MINT_MIN) +
                    METADATA_UPDATE_GAS +
                    DEPLOY_GAS) *
                  estimatedPrice *
                  TOKEN_LOW_PRICE_MULTIPLIER) /
                GWEI_TO_ETH
              ).toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </span>{" "}
            to{" "}
            <span className="text-black">
              {(
                (GasRangeByNetwork[selectedChain][1] *
                  (nftCount * TRANSFER_GAS +
                    Math.max((nftCount * MINT_100_GAS) / 100, MINT_MIN) +
                    METADATA_UPDATE_GAS +
                    DEPLOY_GAS) *
                  Number(tokenPrice?.market_data.ath.usd)) /
                GWEI_TO_ETH
              ).toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </span>{" "}
            depending on gas price and token price at the time of use.
          </p>
        </div>
      </Card>

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight">
        USD Cost Breakdown
      </h3>

      <Card className="p-6 space-y-6">
        <div className="space-y-2">
          <Label className="inline-block">Cost to Deploy Contract in USD</Label>
          <Input
            value={(
              (estimatedGasPrice * DEPLOY_GAS * estimatedPrice) /
              GWEI_TO_ETH
            ).toFixed(2)}
          />
          <p className="text-sm text-muted-foreground">
            Deployment cost for an NFT contract is about 3,110,000 gas units.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="inline-block">
            Cost to Write NFT Metadata On-Chain in USD
          </Label>
          <Input
            value={(
              (estimatedGasPrice * METADATA_UPDATE_GAS * estimatedPrice) /
              GWEI_TO_ETH
            ).toFixed(2)}
          />
          <p className="text-sm text-muted-foreground">
            Average cost for a SKU is about 600,000 gas units. Cost varies based
            on data size such as name, description, image, and animation.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="inline-block">Cost to Mint NFTs in USD</Label>
          <Input
            value={(
              (estimatedGasPrice *
                Math.max((nftCount * MINT_100_GAS) / 100, MINT_MIN) *
                estimatedPrice) /
              GWEI_TO_ETH
            ).toFixed(2)}
          />
          <p className="text-sm text-muted-foreground">
            Average cost for 100 NFTs is about 2,620,000 gas units. Minimum cost
            is about 120,000 gas units regardless of the number of NFTs.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="inline-block">Cost to Transfer NFTs in USD</Label>
          <Input
            value={(
              (estimatedGasPrice * nftCount * TRANSFER_GAS * estimatedPrice) /
              GWEI_TO_ETH
            ).toFixed(2)}
          />
          <p className="text-sm text-muted-foreground">
            Average cost to transfer an NFT is about 100,000 gas units. Each NFT
            is assumed to be transferred once.
          </p>
        </div>

        <div className="flex gap-2 text-muted-foreground">
          <CircleHelp className="w-5 h-5" />
          <p className="text-sm font-medium">
            Cost in USD = gas used x gas price x USD token price. 1 token =
            1,000,000,000 gwei.
          </p>
        </div>
      </Card>
    </div>
  );
};
